/**
 * Teaching Strategies case study: their End-of-School-Year Celebrations
 * 2026 campaign. The full version lives on the Education use-case page
 * (TeachingStrategiesCaseStudy); the home page shows a teaser that links to
 * it (CaseStudyTeaser).
 *
 * Quotes are verbatim recipient replies from the campaign's customer
 * feedback deck, trimmed only at sentence boundaries; only typography is
 * normalized (curly quotes, dashes). Keep the recipients anonymous: no names
 * of people, programs or organizations that would identify who wrote a
 * quote. Mentions of Teaching Strategies ("TSG") and its products (e.g.
 * SmartTeach) are fine; the case study names them openly.
 */

const ASSETS = "/case-studies/teaching-strategies";

export const TS_CUSTOMER = "Teaching Strategies";
export const TS_HOMEPAGE = "https://teachingstrategies.com/";
export const TS_CAMPAIGN = "End-of-School-Year Celebrations 2026";

/** Anchor of the full case study on the Education page. */
export const TS_CASE_STUDY_ID = "teaching-strategies";
export const TS_CASE_STUDY_HREF = `/use-cases/education/#${TS_CASE_STUDY_ID}`;

/** Black wordmark on transparent; invert it on dark backgrounds. */
export const TS_LOGO = {
  src: `${ASSETS}/logo.png`,
  alt: "Teaching Strategies",
  width: 504,
  height: 90,
};

export const TS_HEADLINE = "A personal year-end celebration for every program.";

export interface CaseStudyStat {
  value: string;
  label: string;
}

/** The only two stats either surface shows. */
export const TS_STATS: CaseStudyStat[] = [
  { value: "59%", label: "fully completed, and downloaded badges" },
  { value: "82%", label: "said they’d like it again next year" },
];

/** What each program's narrative covered (full version). */
export const TS_INSIDE = [
  "Teacher engagement across the school year",
  "Implementation progress, step by step",
  "Child outcomes, and their link to teacher engagement",
  "Shout-outs for the program’s standout teachers",
  "Recognition badges to download and share",
];

export const TS_PHOTO = {
  src: `${ASSETS}/classroom.webp`,
  alt: "A preschool teacher and a smiling child with a magnifying glass in a bright classroom.",
  width: 1080,
  height: 855,
};

export const TS_BADGES = [
  { src: `${ASSETS}/badge-literacy.svg`, label: "Literacy" },
  { src: `${ASSETS}/badge-mathematics.svg`, label: "Mathematics" },
  { src: `${ASSETS}/badge-social-emotional.svg`, label: "Social-Emotional" },
];

/** Short enough for the teaser's speech bubble. */
export const TS_TEASER_QUOTE = "OMG!!!!! This is absolutely fantastic.";

export interface CaseStudyQuote {
  text: string;
  /** One phrase of `text`, verbatim, to mark with the yellow highlight. */
  highlight: string;
}

export const TS_QUOTES: CaseStudyQuote[] = [
  {
    text: "I absolutely love this recap of our year!!! Thank you so much for providing the information in such a celebratory way! I hope TSG will continue to provide this recap annually!",
    highlight: "I absolutely love this recap of our year!!!",
  },
  {
    // Trimmed after "wonderful!"
    text: "Thank you! This made my heart smile. This was wonderful!",
    highlight: "This made my heart smile.",
  },
  {
    text: "This celebration clip is awesome! Thank you so much for sharing. I can’t wait to share it with the teachers!",
    highlight: "I can’t wait to share it with the teachers!",
  },
];

export const TS_QUOTES_NOTE = "Feedback from real campaign customers";
